<script lang="ts">
  import { toggleSidebar, sidebarCollapsed } from '../stores/ui'
  import { tStore } from '../i18n'
  import { authState, login, logout } from '../auth/keycloak'
  import LanguageSwitcher from './LanguageSwitcher.svelte'
  import NotificationBell from './NotificationBell.svelte'

  const appName = import.meta.env.VITE_APP_NAME

  function getInitials(name: string | null): string {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/).filter(Boolean)
    return parts
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
  }
</script>

<header class="header">
  <button
    class="header__toggle"
    type="button"
    onclick={toggleSidebar}
    aria-label={$tStore('header.toggleSidebar')}
    aria-expanded={!$sidebarCollapsed}
  >
    {#if $sidebarCollapsed}
      <span class="header__toggle-bar"></span>
      <span class="header__toggle-bar"></span>
      <span class="header__toggle-bar"></span>
    {:else}
      <svg
        class="header__toggle-arrow"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M19 12H5" />
        <path d="M12 19l-7-7 7-7" />
      </svg>
    {/if}
  </button>

  <h1 class="header__title">{appName}</h1>

  <div class="header__spacer"></div>

  <div class="header__user" aria-label={$tStore('header.userMenu')}>
    <LanguageSwitcher />
    <NotificationBell />
    {#if $authState.enabled}
      {#if $authState.authenticated}
        <div
          class="header__account"
          aria-label={$tStore('header.authenticatedAs').replace(
            '{name}',
            $authState.displayName ?? $tStore('header.userFallback'),
          )}
          title={$authState.email ?? $authState.username ?? $authState.displayName ?? undefined}
        >
          <span class="header__avatar" aria-hidden="true">
            {getInitials($authState.displayName)}
          </span>
          <span class="header__user-name">
            {$authState.displayName ?? $tStore('header.userFallback')}
          </span>
        </div>
        <button
          class="header__auth-button"
          type="button"
          aria-label={$tStore('header.logout')}
          title={$tStore('header.logout')}
          onclick={() => void logout()}
        >
          <svg
            class="header__auth-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M10 17l5-5-5-5" />
            <path d="M15 12H3" />
            <path d="M21 4v16" />
          </svg>
          <span>{$tStore('header.logout')}</span>
        </button>
      {:else}
        <button
          class="header__auth-button"
          type="button"
          aria-label={$tStore('header.login')}
          onclick={() => void login()}
        >
          <svg
            class="header__auth-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M14 17l5-5-5-5" />
            <path d="M19 12H7" />
            <path d="M3 4v16" />
          </svg>
          <span>{$tStore('header.login')}</span>
        </button>
      {/if}
    {/if}
  </div>
</header>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .header {
    display: flex;
    align-items: center;
    gap: v.$space-md;
    height: v.$header-height;
    padding: 0 v.$space-lg;
    background: v.$color-header-bg;
    backdrop-filter: saturate(180%) blur(8px);
    -webkit-backdrop-filter: saturate(180%) blur(8px);
    color: v.$color-header-fg;
    border-bottom: 1px solid v.$color-border;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .header__toggle {
    display: inline-flex;
    flex-direction: column;
    justify-content: space-between;
    width: 28px;
    height: 28px;
    padding: 6px;
    background: none;
    border: 1px solid transparent;
    border-radius: v.$radius-sm;
    cursor: pointer;
    color: v.$color-text-muted;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;

    &:hover {
      background: v.$color-surface;
      border-color: v.$color-border;
      color: v.$color-text;
    }
  }

  .header__toggle-bar {
    display: block;
    width: 100%;
    height: 2px;
    background: currentColor;
    border-radius: 1px;
  }

  .header__toggle-arrow {
    width: 100%;
    height: 100%;
  }

  .header__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: v.$color-text-muted;
  }

  .header__spacer {
    flex: 1;
  }

  .header__user {
    display: inline-flex;
    align-items: center;
    gap: v.$space-sm;
    min-width: 0;
  }

  .header__account {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    min-width: 0;
    max-width: 220px;
  }

  .header__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    border-radius: v.$radius-sm;
    background: v.$color-primary-soft;
    color: v.$color-primary-hover;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .header__user-name {
    overflow: hidden;
    color: v.$color-text;
    font-size: 0.875rem;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header__auth-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: v.$space-xs;
    min-height: 32px;
    padding: 0 v.$space-sm;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface-elevated;
    color: v.$color-text-muted;
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 600;
    white-space: nowrap;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;

    &:hover {
      background: v.$color-surface;
      border-color: v.$color-border-strong;
      color: v.$color-text;
    }
  }

  .header__auth-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  @media (max-width: v.$bp-sm) {
    .header__account {
      max-width: 44px;
    }

    .header__user-name,
    .header__auth-button span {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .header__auth-button {
      width: 32px;
      padding: 0;
    }
  }
</style>
